import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Check, Edit, Loader2, Mail, Pencil, Plus, Trash2, X } from 'lucide-react';
import SelectField from './SelectField';
import { isContactUnsubscribed, isContactBounced, formatUnsubscribedAt, withNormalizedSubscription } from '../utils/contactUnsubscribe';

const CATEGORY_COLOR_PRESETS = [
  '#0F766E',
  '#1D4ED8',
  '#7C3AED',
  '#BE185D',
  '#C2410C',
  '#A16207',
  '#475569',
  '#15803D',
];

const getInitials = (nameOrEmail) => {
  if (!nameOrEmail) return '?';
  const base = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0] : nameOrEmail;
  const parts = base.trim().split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
};

const genderLabel = (g) => {
  const v = String(g || '').toLowerCase();
  if (v === 'male' || v === 'm') return 'Männlich';
  if (v === 'female' || v === 'f') return 'Weiblich';
  return 'Unbekannt';
};

const DETAIL_FIELDS = {
  name: { key: 'name', label: 'Ansprechpartner', type: 'text' },
  gender: { key: 'gender', label: 'Geschlecht', type: 'gender' },
  name2: { key: 'name2', label: '2. Ansprechpartner', type: 'text' },
  gender2: { key: 'gender2', label: 'Geschlecht', type: 'gender' },
  email: { key: 'email', label: 'E-Mail', type: 'email' },
  company: { key: 'company', label: 'Unternehmen', type: 'text' },
  phone: { key: 'phone', label: 'Telefon', type: 'tel' },
  website: { key: 'website', label: 'Website', type: 'text' },
  notes: { key: 'notes', label: 'Notizen', type: 'textarea' },
};

/**
 * Shared Kontaktdetails modal (inline field edit + footer actions).
 */
const ContactDetailModal = ({
  contact,
  loading = false,
  zClassName = 'z-[100]',
  onClose,
  onUpdated,
  onDelete,
  onHistory,
  onEdit,
}) => {
  const [editingField, setEditingField] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [subscriptionBusy, setSubscriptionBusy] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoryBusy, setCategoryBusy] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState(CATEGORY_COLOR_PRESETS[0]);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const inputRef = useRef(null);
  const categoryPickerRef = useRef(null);

  useEffect(() => {
    setEditingField(null);
    setDraft('');
    setSaving(false);
    setShowNewCategory(false);
    setShowCategoryPicker(false);
    setCategorySearch('');
    setNewCategoryName('');
  }, [contact?.id]);

  useEffect(() => {
    let cancelled = false;
    const loadCategories = async () => {
      setCategoriesLoading(true);
      try {
        const res = await axios.get('/api/contact-categories');
        if (!cancelled) {
          setAllCategories(Array.isArray(res.data?.categories) ? res.data.categories : []);
        }
      } catch (_) {
        if (!cancelled) setAllCategories([]);
      } finally {
        if (!cancelled) setCategoriesLoading(false);
      }
    };
    loadCategories();
    return () => {
      cancelled = true;
    };
  }, [contact?.id]);

  useEffect(() => {
    if (!showCategoryPicker) return undefined;
    const onDown = (e) => {
      if (categoryPickerRef.current?.contains(e.target)) return;
      setShowCategoryPicker(false);
      setShowNewCategory(false);
      setCategorySearch('');
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setShowCategoryPicker(false);
        setShowNewCategory(false);
        setCategorySearch('');
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showCategoryPicker]);

  useEffect(() => {
    if (!editingField || !inputRef.current) return;
    const el = inputRef.current;
    el.focus();
    if (typeof el.select === 'function' && el.tagName !== 'SELECT') {
      el.select();
    }
  }, [editingField]);

  if (!contact) return null;

  const optedOut = isContactUnsubscribed(contact);
  const bounced = isContactBounced(contact);
  const unsubscribedLabel = formatUnsubscribedAt(
    contact.unsubscribed_at || contact.contact_unsubscribed_at
  );

  const setCampaignSubscription = async (subscribed) => {
    if (!contact?.id || subscriptionBusy || loading) return;
    setSubscriptionBusy(true);
    try {
      const res = await axios.patch(`/api/contacts/${contact.id}/campaign-subscription`, {
        subscribed,
      });
      const fromApi = res.data?.contact || {};
      const updated = withNormalizedSubscription({
        ...contact,
        ...fromApi,
        id: Number(fromApi.id || contact.id),
        unsubscribed: subscribed ? 0 : 1,
        unsubscribed_at: subscribed ? null : fromApi.unsubscribed_at || res.data?.unsubscribed_at || null,
      });
      onUpdated?.(updated);
      toast.success(
        res.data?.message ||
          (subscribed ? 'Kampagnen-Abo wiederhergestellt' : 'Kampagnen-Abo deaktiviert')
      );
      window.dispatchEvent(
        new CustomEvent('odium:contacts-changed', {
          detail: { contactId: updated.id, unsubscribed: updated.unsubscribed },
        })
      );
      window.dispatchEvent(
        new CustomEvent('odium:campaigns-changed', {
          detail: { contactId: updated.id, reason: 'subscription' },
        })
      );
    } catch (error) {
      toast.error(error.response?.data?.error || 'Abo-Status konnte nicht geändert werden');
    } finally {
      setSubscriptionBusy(false);
    }
  };

  const buildContactPayload = (overrides = {}) => ({
    name: contact.name || '',
    email: contact.email || '',
    company: contact.company || '',
    phone: contact.phone || '',
    website: contact.website || '',
    notes: contact.notes || '',
    gender: contact.gender || 'unknown',
    name2: contact.name2 || '',
    gender2: contact.gender2 || 'unknown',
    categoryIds: Array.isArray(contact.categories)
      ? contact.categories.map((c) => c.id).filter(Boolean)
      : [],
    ...overrides,
  });

  const persistContact = async (payload) => {
    const res = await axios.put(`/api/contacts/${contact.id}`, payload);
    const categories =
      Array.isArray(res.data?.categories)
        ? res.data.categories
        : Array.isArray(payload.categoryIds)
          ? allCategories.filter((c) => payload.categoryIds.map(Number).includes(Number(c.id)))
          : contact.categories || [];
    const updated = {
      ...contact,
      ...payload,
      categories,
    };
    delete updated.categoryIds;
    onUpdated?.(updated);
    window.dispatchEvent(new CustomEvent('odium:contacts-changed'));
    return updated;
  };

  const selectedCategoryIds = new Set(
    (Array.isArray(contact.categories) ? contact.categories : [])
      .map((c) => Number(c.id))
      .filter(Boolean)
  );

  const assignedCategories = allCategories.filter((c) =>
    selectedCategoryIds.has(Number(c.id))
  );

  const filteredPickerCategories = (() => {
    const q = String(categorySearch || '').trim().toLowerCase();
    const list = [...allCategories].sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), 'de', { sensitivity: 'base' })
    );
    if (!q) return list;
    return list.filter((c) => String(c.name || '').toLowerCase().includes(q));
  })();

  const toggleCategory = async (categoryId) => {
    if (!contact?.id || categoryBusy || loading) return;
    const id = Number(categoryId);
    if (!id) return;
    const nextIds = selectedCategoryIds.has(id)
      ? [...selectedCategoryIds].filter((x) => x !== id)
      : [...selectedCategoryIds, id];
    setCategoryBusy(true);
    try {
      await persistContact(buildContactPayload({ categoryIds: nextIds }));
      toast.success(selectedCategoryIds.has(id) ? 'Kategorie entfernt' : 'Kategorie hinzugefügt');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Kategorie konnte nicht gespeichert werden');
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleCreateCategory = async () => {
    const name = String(newCategoryName || '').trim();
    if (!name) {
      toast.error('Name für die Kategorie fehlt');
      return;
    }
    if (!contact?.id || categoryBusy) return;
    setCategoryBusy(true);
    try {
      const res = await axios.post('/api/contact-categories', {
        name,
        color: newCategoryColor,
      });
      const created = res.data?.category;
      if (!created?.id) throw new Error('Keine Kategorie zurückgegeben');
      const nextAll = [...allCategories, created].sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), 'de', { sensitivity: 'base' })
      );
      setAllCategories(nextAll);
      await persistContact(
        buildContactPayload({
          categoryIds: [...selectedCategoryIds, created.id],
        })
      );
      setNewCategoryName('');
      setNewCategoryColor(
        CATEGORY_COLOR_PRESETS[nextAll.length % CATEGORY_COLOR_PRESETS.length]
      );
      setShowNewCategory(false);
      toast.success('Kategorie erstellt und zugewiesen');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Kategorie konnte nicht erstellt werden');
    } finally {
      setCategoryBusy(false);
    }
  };

  const startEdit = (field) => {
    if (!contact?.id || loading || saving) return;
    const raw = contact[field];
    setEditingField(field);
    setDraft(field === 'gender' || field === 'gender2' ? (raw || 'unknown') : (raw || ''));
  };

  const cancelEdit = () => {
    if (saving) return;
    setEditingField(null);
    setDraft('');
  };

  const saveField = async (overrideValue) => {
    if (!contact?.id || !editingField || saving) return;
    const field = editingField;
    let nextValue = overrideValue !== undefined ? overrideValue : draft;
    if (typeof nextValue === 'string' && field !== 'notes') {
      nextValue = nextValue.trim();
    } else if (typeof nextValue === 'string' && field === 'notes') {
      nextValue = nextValue.replace(/\s+$/, '');
    }

    if ((field === 'name' || field === 'email') && !nextValue) {
      toast.error(field === 'name' ? 'Name darf nicht leer sein' : 'E-Mail darf nicht leer sein');
      return;
    }

    const prev =
      field === 'gender' || field === 'gender2'
        ? String(contact[field] || 'unknown')
        : String(contact[field] || '');
    if (prev === String(nextValue || '')) {
      cancelEdit();
      return;
    }

    const payload = buildContactPayload({ [field]: nextValue });
    if (field === 'name2' && !String(nextValue || '').trim()) {
      payload.gender2 = 'unknown';
    }

    setSaving(true);
    try {
      await persistContact(payload);
      setEditingField(null);
      setDraft('');
      toast.success('Gespeichert');
    } catch (error) {
      if (error.response?.data?.error?.includes('UNIQUE')) {
        toast.error('Diese E-Mail-Adresse existiert bereits');
      } else {
        toast.error(error.response?.data?.error || 'Speichern fehlgeschlagen');
      }
    } finally {
      setSaving(false);
    }
  };

  const renderField = (row, { compact = false } = {}) => {
    if (!row) return null;
    const raw = contact[row.key];
    const display =
      row.key === 'gender' || row.key === 'gender2' ? genderLabel(raw) : (raw || '');
    const isEditing = editingField === row.key;
    const href =
      row.key === 'email' && raw
        ? `mailto:${raw}`
        : row.key === 'phone' && raw
          ? `tel:${raw}`
          : row.key === 'website' && raw
            ? (String(raw).startsWith('http') ? raw : `https://${raw}`)
            : null;

    return (
      <div
        key={row.key}
        className={`rounded-xl border px-3.5 py-2 transition-colors ${
          isEditing
            ? 'border-primary-300 dark:border-primary-700 bg-primary-50/40 dark:bg-primary-950/20'
            : 'border-neutral-200 dark:border-neutral-800 hover:border-neutral-300 dark:hover:border-neutral-700'
        }`}
      >
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <p className="text-[10px] uppercase tracking-wide text-neutral-400">{row.label}</p>
          {!isEditing ? (
            <button
              type="button"
              onClick={() => startEdit(row.key)}
              className="p-1 rounded-md text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              title={`${row.label} bearbeiten`}
              aria-label={`${row.label} bearbeiten`}
            >
              <Pencil className="w-3 h-3" />
            </button>
          ) : (
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                disabled={saving}
                onClick={() => saveField()}
                className="p-1 rounded-md text-primary-600 hover:bg-primary-100 dark:hover:bg-primary-950/40 disabled:opacity-50"
                aria-label="Speichern"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={cancelEdit}
                className="p-1 rounded-md text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-50"
                aria-label="Abbrechen"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {isEditing ? (
          row.type === 'gender' ? (
            <SelectField
              wrapperClassName="mt-0.5"
              ref={inputRef}
              value={draft || 'unknown'}
              disabled={saving}
              onChange={(e) => {
                const value = e.target.value;
                setDraft(value);
                saveField(value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelEdit();
                }
              }}
              className="input-field !py-1.5 !px-2.5 text-sm"
              chevronSize="w-3.5 h-3.5"
            >
              <option value="unknown">Keine Angabe</option>
              <option value="male">Männlich</option>
              <option value="female">Weiblich</option>
            </SelectField>
          ) : row.type === 'textarea' ? (
            <textarea
              ref={inputRef}
              value={draft}
              disabled={saving}
              rows={3}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelEdit();
                }
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  saveField();
                }
              }}
              className="input-field !py-1.5 !px-2.5 text-sm mt-0.5 resize-none"
              placeholder="Notizen hinzufügen…"
            />
          ) : (
            <input
              ref={inputRef}
              type={row.type}
              value={draft}
              disabled={saving}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveField();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelEdit();
                }
              }}
              className="input-field !py-1.5 !px-2.5 text-sm mt-0.5"
              placeholder={`${row.label} eingeben…`}
            />
          )
        ) : display ? (
          href ? (
            <div className="flex items-start justify-between gap-2">
              <a
                href={href}
                {...(row.key === 'website' ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                onClick={(e) => e.stopPropagation()}
                className={`text-sm text-primary-700 dark:text-primary-300 hover:underline break-words min-w-0 ${
                  row.type === 'textarea' ? 'whitespace-pre-wrap' : ''
                }`}
              >
                {display}
              </a>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => startEdit(row.key)}
              className={`w-full text-left text-sm text-neutral-900 dark:text-neutral-100 break-words ${
                row.type === 'textarea' ? 'whitespace-pre-wrap' : ''
              } ${compact ? 'truncate' : ''}`}
            >
              {display}
            </button>
          )
        ) : (
          <button
            type="button"
            onClick={() => startEdit(row.key)}
            className="w-full text-left text-sm text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
          >
            —
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={`fixed inset-0 ${zClassName} flex items-end sm:items-center justify-center p-0 sm:p-4`}>
      <div className="absolute inset-0 bg-neutral-950/60 backdrop-blur-sm" onClick={onClose} />
      <div
        className={`relative bg-white dark:bg-neutral-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden border ${
          optedOut
            ? 'border-red-300 dark:border-red-800'
            : 'border-neutral-200 dark:border-neutral-800'
        }`}
      >
        <div
          className={`flex-shrink-0 px-4 sm:px-5 pt-3 sm:pt-4 pb-4 border-b flex flex-col gap-3 ${
            optedOut
              ? 'border-red-200 dark:border-red-900/60 bg-red-50/80 dark:bg-red-950/30'
              : 'border-neutral-200 dark:border-neutral-800'
          }`}
        >
          <div className="mx-auto h-1 w-10 rounded-full bg-neutral-200 dark:bg-neutral-700 sm:hidden" />
          <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex items-start gap-3">
            <div
              className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
                optedOut
                  ? 'bg-red-200 dark:bg-red-900/60 text-red-800 dark:text-red-200'
                  : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300'
              }`}
            >
              {getInitials(contact.name || contact.email)}
            </div>
            <div className="min-w-0 flex-1">
              <p
                className={`text-xs font-medium mb-0.5 ${
                  optedOut
                    ? 'text-red-700 dark:text-red-300'
                    : 'text-primary-600 dark:text-primary-400'
                }`}
              >
                {optedOut ? 'Abgemeldet' : bounced ? 'Unzustellbar' : 'Kontaktdetails'}
              </p>
              {loading ? (
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white truncate">Laden…</h2>
              ) : (
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white truncate">
                  {contact.name2
                    ? `${contact.name || contact.email || 'Kontakt'} & ${contact.name2}`
                    : contact.name || contact.email || 'Kontakt'}
                </h2>
              )}
              {contact.company && !loading ? (
                <p className="text-xs text-neutral-500 truncate">{contact.company}</p>
              ) : null}
              {!loading ? (
                <div className="mt-1.5 relative" ref={categoryPickerRef}>
                  <div className="flex flex-wrap items-center gap-1">
                    {categoriesLoading ? (
                      <span className="text-[10px] text-neutral-400 inline-flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Kategorien…
                      </span>
                    ) : assignedCategories.length ? (
                      assignedCategories.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          disabled={categoryBusy}
                          onClick={() => toggleCategory(cat.id)}
                          className="inline-flex items-center gap-1 rounded-full pl-1.5 pr-1 py-0.5 text-[10px] font-medium text-white border border-transparent transition-opacity disabled:opacity-50 hover:opacity-90"
                          style={{ backgroundColor: cat.color || '#0F766E' }}
                          title="Kategorie entfernen"
                        >
                          <span className="max-w-[9rem] truncate">{cat.name}</span>
                          <X className="w-2.5 h-2.5 opacity-80 flex-shrink-0" />
                        </button>
                      ))
                    ) : (
                      <span className="text-[10px] text-neutral-400">Keine Kategorie</span>
                    )}
                    <button
                      type="button"
                      disabled={categoryBusy || categoriesLoading}
                      onClick={() => {
                        setShowCategoryPicker((v) => !v);
                        setShowNewCategory(false);
                        setCategorySearch('');
                      }}
                      className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium text-primary-600 dark:text-primary-400 border border-primary-200/70 dark:border-primary-800/60 hover:bg-primary-50 dark:hover:bg-primary-950/40 disabled:opacity-50"
                      title="Kategorie zuweisen"
                    >
                      <Plus className="w-3 h-3" />
                      Kategorie
                    </button>
                  </div>

                  {showCategoryPicker ? (
                    <div className="absolute left-0 right-0 sm:right-auto sm:min-w-[16rem] z-20 mt-1.5 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-xl shadow-stone-900/10 dark:shadow-black/40 overflow-hidden">
                      <div className="p-2 border-b border-neutral-100 dark:border-neutral-800">
                        <input
                          type="search"
                          value={categorySearch}
                          onChange={(e) => setCategorySearch(e.target.value)}
                          placeholder="Kategorie suchen…"
                          className="input-field !py-1.5 !px-2.5 text-[11px] w-full"
                          autoFocus
                        />
                      </div>
                      <div className="max-h-44 overflow-y-auto py-1">
                        {filteredPickerCategories.length ? (
                          filteredPickerCategories.map((cat) => {
                            const active = selectedCategoryIds.has(Number(cat.id));
                            return (
                              <button
                                key={cat.id}
                                type="button"
                                disabled={categoryBusy}
                                onClick={() => toggleCategory(cat.id)}
                                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-[11px] transition-colors disabled:opacity-50 ${
                                  active
                                    ? 'bg-primary-50/80 dark:bg-primary-950/40 text-neutral-900 dark:text-neutral-100'
                                    : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800/80'
                                }`}
                              >
                                <span
                                  className="w-2 h-2 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: cat.color || '#0F766E' }}
                                />
                                <span className="min-w-0 flex-1 truncate">{cat.name}</span>
                                {active ? (
                                  <Check className="w-3 h-3 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                                ) : null}
                              </button>
                            );
                          })
                        ) : (
                          <p className="px-2.5 py-2 text-[11px] text-neutral-400">
                            Keine Treffer
                          </p>
                        )}
                      </div>
                      <div className="border-t border-neutral-100 dark:border-neutral-800 p-2">
                        {showNewCategory ? (
                          <div className="space-y-1.5">
                            <input
                              type="text"
                              value={newCategoryName}
                              disabled={categoryBusy}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleCreateCategory();
                                }
                              }}
                              className="input-field !py-1 !px-2 text-[11px] w-full"
                              placeholder="Neue Kategorie"
                              autoFocus
                            />
                            <div className="flex items-center gap-1 flex-wrap">
                              {CATEGORY_COLOR_PRESETS.slice(0, 6).map((color) => (
                                <button
                                  key={color}
                                  type="button"
                                  disabled={categoryBusy}
                                  onClick={() => setNewCategoryColor(color)}
                                  className={`w-3.5 h-3.5 rounded-full border ${
                                    newCategoryColor === color
                                      ? 'border-black dark:border-white'
                                      : 'border-transparent'
                                  }`}
                                  style={{ backgroundColor: color }}
                                  aria-label={`Farbe ${color}`}
                                />
                              ))}
                              <button
                                type="button"
                                disabled={categoryBusy || !String(newCategoryName || '').trim()}
                                onClick={handleCreateCategory}
                                className="ml-auto text-[10px] font-medium text-primary-600 disabled:opacity-50"
                              >
                                {categoryBusy ? '…' : 'Anlegen'}
                              </button>
                              <button
                                type="button"
                                disabled={categoryBusy}
                                onClick={() => {
                                  setShowNewCategory(false);
                                  setNewCategoryName('');
                                }}
                                className="text-[10px] text-neutral-400"
                              >
                                Abbrechen
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            disabled={categoryBusy}
                            onClick={() => setShowNewCategory(true)}
                            className="w-full inline-flex items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-950/40 disabled:opacity-50"
                          >
                            <Plus className="w-3 h-3" />
                            Neue Kategorie
                          </button>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost p-1.5" aria-label="Schließen">
            <X className="w-5 h-5" />
          </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-5 py-4 space-y-2.5">
          {!loading ? (
            <div
              className={`rounded-xl border px-3.5 py-3 ${
                optedOut
                  ? 'border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-950/40'
                  : 'border-neutral-200 dark:border-neutral-800 bg-neutral-50/80 dark:bg-neutral-950/30'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p
                    className={`text-sm font-medium ${
                      optedOut
                        ? 'text-red-800 dark:text-red-200'
                        : 'text-neutral-900 dark:text-neutral-100'
                    }`}
                  >
                    Kampagnen-Abo {optedOut ? 'deaktiviert' : 'aktiv'}
                  </p>
                  <p
                    className={`mt-0.5 text-xs ${
                      optedOut
                        ? 'text-red-700/90 dark:text-red-300/90'
                        : 'text-neutral-500 dark:text-neutral-400'
                    }`}
                  >
                    {optedOut
                      ? `${unsubscribedLabel ? `Austragung am ${unsubscribedLabel}. ` : ''}Keine weiteren Kampagnen-Mails.`
                      : bounced
                        ? 'Diese Adresse ist unzustellbar. Follow-ups an diesen Kontakt sind gestoppt. Nach Änderung der E-Mail kann wieder versendet werden.'
                        : 'Kontakt kann Kampagnen und Follow-ups erhalten.'}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={subscriptionBusy || !contact?.id}
                  onClick={() => setCampaignSubscription(optedOut)}
                  className={`flex-shrink-0 inline-flex items-center rounded-lg px-2.5 py-1.5 text-[11px] font-medium transition-colors disabled:opacity-50 ${
                    optedOut
                      ? 'bg-white dark:bg-neutral-900 text-red-800 dark:text-red-200 border border-red-300 dark:border-red-800 hover:bg-red-100/80 dark:hover:bg-red-900/40'
                      : 'bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-200 border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                  }`}
                >
                  {subscriptionBusy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : optedOut ? (
                    'Wiederherstellen'
                  ) : (
                    'Deaktivieren'
                  )}
                </button>
              </div>
            </div>
          ) : null}
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-neutral-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Kontakt wird geladen…
            </div>
          ) : (
            <div className="space-y-2">
              <div
                className={`grid gap-2 items-stretch ${
                  String(contact.name || '').trim() || editingField === 'gender'
                    ? 'sm:grid-cols-[minmax(0,1fr)_11.5rem]'
                    : 'grid-cols-1'
                }`}
              >
                {renderField(DETAIL_FIELDS.name)}
                {String(contact.name || '').trim() || editingField === 'gender'
                  ? renderField(DETAIL_FIELDS.gender, { compact: true })
                  : null}
              </div>
              <div
                className={`grid gap-2 items-stretch ${
                  String(contact.name2 || '').trim() || editingField === 'gender2'
                    ? 'sm:grid-cols-[minmax(0,1fr)_11.5rem]'
                    : 'grid-cols-1'
                }`}
              >
                {renderField(DETAIL_FIELDS.name2)}
                {String(contact.name2 || '').trim() || editingField === 'gender2'
                  ? renderField(DETAIL_FIELDS.gender2, { compact: true })
                  : null}
              </div>
              {renderField(DETAIL_FIELDS.email)}
              {renderField(DETAIL_FIELDS.company)}
              {renderField(DETAIL_FIELDS.phone)}
              {renderField(DETAIL_FIELDS.website)}
              {renderField(DETAIL_FIELDS.notes)}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 px-4 sm:px-5 py-3 sm:py-4 border-t border-neutral-200 dark:border-neutral-800 flex flex-col-reverse sm:flex-row flex-wrap gap-2 sm:justify-end bg-neutral-50/80 dark:bg-neutral-950/40 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-4">
          {onDelete ? (
            <button
              type="button"
              disabled={loading || !contact?.id}
              className="inline-flex items-center justify-center rounded-lg border border-danger-200 dark:border-danger-900/40 text-danger-700 dark:text-danger-300 text-sm font-medium px-3.5 py-2.5 sm:py-2 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors sm:mr-auto disabled:opacity-50 w-full sm:w-auto"
              onClick={() => onDelete(contact)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Löschen
            </button>
          ) : null}
          {onHistory ? (
            <button
              type="button"
              disabled={loading || !contact?.id}
              className="btn-secondary w-full sm:w-auto justify-center"
              onClick={() => onHistory(contact)}
            >
              <Mail className="w-4 h-4 mr-2" />
              E-Mail-Verlauf
            </button>
          ) : null}
          {onEdit ? (
            <button
              type="button"
              disabled={loading || !contact?.id}
              className="btn-primary w-full sm:w-auto justify-center"
              onClick={() => onEdit(contact)}
            >
              <Edit className="w-4 h-4 mr-2" />
              Bearbeiten
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ContactDetailModal;
